import { router, useNavigation } from 'expo-router';
import { useLayoutEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { StyleSheet, View } from 'react-native';

import { useEditIndividualName } from '@/api/hooks/individualHooks';
import { useGetMeMutation } from '@/api/hooks/meHooks';
import BrandedButton from '@/components/buttons/BrandedButton';
import GoBackButton from '@/components/buttons/GoBackButton';
import FormWrapper from '@/components/forms/FormWrapper';
import GuardWrapper from '@/components/GuardWrapper';
import TextInputField from '@/components/inputs/TextInputField';
import Text from '@/components/Text';
import Title from '@/components/Title';
import { useAuth } from '@/contexts/authContext';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { EditNameData, editNameResolver } from '@/lib/validations/EditName';
import { i18n } from '@/utils/i18n';

const EditName = () => {
  const navigation = useNavigation();
  const { state, setCurrentUser } = useAuth();
  const { showSuccess, showError } = useEndFlowModal();
  const { mutateAsync: getMe } = useGetMeMutation();

  const { control, handleSubmit } = useForm<EditNameData>({
    defaultValues: {
      name: state.user?.user?.firstName,
      lastname: state.user?.user?.lastName
    },
    mode: 'onChange',
    resolver: editNameResolver
  });

  const { mutateAsync: editIndividual, isPending } = useEditIndividualName();

  //Locales
  const title = i18n.t('settings.edit_name.title');
  const advice = i18n.t('settings.edit_name.advice');
  const name_placeholder = i18n.t('settings.edit_name.name_placeholder');
  const lastname_placeholder = i18n.t('settings.edit_name.lastname_placeholder');
  const success = i18n.t('end_flow.success_title');
  const success_message = i18n.t('settings.edit_name.success_message');
  const failure = i18n.t('end_flow.error_title');
  const failure_message = i18n.t('settings.edit_name.failure_message');
  const save = i18n.t('mix.save');

  const editName: SubmitHandler<EditNameData> = async (data) => {
    await editIndividual(
      {
        userId: state.user?.user?.id ?? '',
        firstName: data.name,
        lastName: data.lastname
      },
      {
        onSuccess: async () => {
          await getMe(
            { firebaseId: state.user?.user?.firebaseId ?? '' },
            {
              onSuccess: (data) => {
                setCurrentUser(data.user);
              }
            }
          );
          showSuccess({
            title: success,
            message: success_message
          });
          router.back();
        },
        onError: () => {
          showError({
            title: failure,
            message: failure_message
          });
        }
      }
    );
  };

  useLayoutEffect(() => {
    navigation.setOptions({
      headerTitle: () => <Title label={title} />,
      headerLeft: () => <GoBackButton />
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigation]);

  return (
    <GuardWrapper canAccess={['INDIVIDUAL']}>
      <FormWrapper>
        <View style={styles.container}>
          {/* ToDO: Uncomment if the backend allows to edit the name only once per year*/}
          {/* <Text style={styles.text} fontWeight="450">
            {advice}
          </Text> */}
          <TextInputField name="name" control={control} placeholder={name_placeholder} />
          <TextInputField name="lastname" control={control} placeholder={lastname_placeholder} />
          <BrandedButton onPress={handleSubmit(editName)} loading={isPending}>
            <Text style={styles.btnText} fontWeight="700">
              {save}
            </Text>
          </BrandedButton>
        </View>
      </FormWrapper>
    </GuardWrapper>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'column',
    rowGap: 25,
    paddingVertical: 18
  },
  text: {
    fontSize: 14,
    color: 'rgba(0,0,0,0.6)'
  },
  btnText: {
    fontSize: 14,
    color: 'white'
  }
});

export default EditName;
